import React, { useRef, useState } from 'react';
import { getIcon } from '../Icons';
import { TooltipTrigger } from '../TooltipTrigger/TooltipTrigger';
import classnames from 'classnames';
import './ExtendedTooltip.scss';

interface ExtendedTooltipProps {
  email?: string;
  website?: string;
  maintainer: string;
  subtitle?: string;
  titleIcon: string;
  basicFirstIcon: string;
  basicSecondIcon: string;
  detail?: boolean;
}

export default function ExtendedTooltip({
  email,
  website,
  subtitle,
  maintainer,
  basicFirstIcon,
  basicSecondIcon,
  titleIcon,
  detail,
}: ExtendedTooltipProps): React.ReactElement {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsTooltipOpen(false);
    }, 200);
  };

  function parseMaintainers(maintainerString) {
    return maintainerString.split(/\s*,\s*/).filter(Boolean);
  }

  const maintainersArray = parseMaintainers(maintainer);
  const maintainerCount = maintainersArray.length;
  if (!email || !website) {
    const isManyMaintainers = maintainerCount > 3;
    const displayLabel = isManyMaintainers ? `${maintainerCount} Maintainers` : maintainersArray.join(', ');
    const tooltipText = isManyMaintainers ? (
      <div>
        {maintainersArray.map((name, index) => (
          <div key={index}>{name}</div>
        ))}
      </div>
    ) : (
      'Maintainer'
    );

    return (
      <TooltipTrigger
        TriggerElement={
          <span className={classnames('library-record__maintainer-tooltip', { 'library-record__maintainer-tooltip--dust': detail })}>
            <span className="library-record__icon">{getIcon(titleIcon)}</span>
            {displayLabel}
          </span>
        }
        delay={0}
        tooltipPlacement="top"
        tooltipStrategy="absolute"
        tooltipType="tooltip"
      >
        {tooltipText}
      </TooltipTrigger>
    );
  }

  return (
    <div className="extended-tooltip-container" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <span className="library-record__maintainer-tooltip extended-tooltip__simple-title">
        <span className="library-record__icon">{getIcon(titleIcon)}</span>
        {maintainer}
      </span>
      {isTooltipOpen && (
        <div className="extended-tooltip">
          <div className="extended-tooltip__title">{maintainer}</div>
          <div className="extended-tooltip__subtitle">{subtitle}</div>
          <hr />
          <div className="extended-tooltip__box">
            <span className="extended-tooltip__icon">{getIcon(basicFirstIcon)}</span>
            <span className="extended-tooltip__name">Website</span>
            <a href={website} target="_blank" rel="noopener noreferrer" className="extended-tooltip__info">
              {website}
            </a>
          </div>
          <div className="extended-tooltip__box">
            <span className="extended-tooltip__icon">{getIcon(basicSecondIcon)}</span>
            <span className="extended-tooltip__name">Email</span>
            <a href={`mailto:${email}`} className="extended-tooltip__info">
              {email}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
